"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SecureScaleCommandDirectory = exports.SecureScaleCommandDirectoryPayload = exports.SecureScaleLocalCommandProof = exports.SecureScaleLocalCommandPayload = exports.SecureScaleCommandEnrollment = exports.SecureScaleCommandEnrollmentChallenge = exports.SecureScaleCommandDelegation = exports.SecureScaleCommandDelegationPayload = void 0;
const zod_1 = require("zod");
const securescale_attendant_js_1 = require("./securescale-attendant.js");
const Id = zod_1.z.string().trim().min(1).max(128);
const Revision = zod_1.z.string().regex(/^(0|[1-9]\d*)$/);
const Instant = zod_1.z.string().datetime();
const PublicKey = zod_1.z.string().regex(/^[A-Za-z0-9_-]{43}$/);
const Signature = zod_1.z.string().regex(/^[A-Za-z0-9+/]{86}==$/);
exports.SecureScaleCommandDelegationPayload = zod_1.z
    .object({
    kind: zod_1.z.literal('securescale-station-command-delegation-v1'),
    issuer: zod_1.z.literal('reform-bos'),
    audience: zod_1.z.literal('securescale-local-requester'),
    tenantId: Id,
    storeId: Id,
    stationId: Id,
    publicKey: PublicKey,
    revision: Revision,
    revocationFloor: Revision,
    issuedAt: Instant,
    expiresAt: Instant,
})
    .strict()
    .superRefine((v, c) => {
    const duration = Date.parse(v.expiresAt) - Date.parse(v.issuedAt);
    if (duration <= 0 || duration > securescale_attendant_js_1.SECURESCALE_ATTENDANT_MAX_LIFETIME_MS)
        c.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['expiresAt'],
            message: 'Delegation must be positive and at most96hours',
        });
    if (BigInt(v.revocationFloor) > BigInt(v.revision))
        c.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['revocationFloor'],
            message: 'Invalid revocation floor',
        });
});
exports.SecureScaleCommandDelegation = zod_1.z
    .object({
    kind: zod_1.z.literal('securescale-station-command-delegation-signed-v1'),
    payload: exports.SecureScaleCommandDelegationPayload,
    kid: Id,
    signature: Signature,
})
    .strict();
exports.SecureScaleCommandEnrollmentChallenge = zod_1.z
    .object({
    kind: zod_1.z.literal('securescale-station-command-enrollment-v1'),
    tenantId: Id,
    storeId: Id,
    stationId: Id,
    nonce: zod_1.z.string().uuid(),
    issuedAt: Instant,
    expiresAt: Instant,
})
    .strict();
/** Signature of canonicalSHA256({kind:'securescale-station-key-possession-v1',challenge,publicKey}). */
exports.SecureScaleCommandEnrollment = zod_1.z
    .object({
    challenge: exports.SecureScaleCommandEnrollmentChallenge,
    publicKey: PublicKey,
    signature: Signature,
})
    .strict();
exports.SecureScaleLocalCommandPayload = zod_1.z
    .object({
    kind: zod_1.z.literal('securescale-local-command-v1'),
    tenantId: Id,
    storeId: Id,
    stationId: Id,
    targetTerminalId: Id,
    commandUuid: zod_1.z.string().uuid(),
    callId: Id,
    nonce: zod_1.z.string().uuid(),
    sessionId: zod_1.z.string().uuid(),
    sessionExpiresAt: Instant,
    requestExpiresAt: Instant,
    requestOrigin: zod_1.z
        .object({ tenantId: Id, storeId: Id, terminalId: Id, versionId: Id, basketId: Id })
        .strict(),
    observationSha256: zod_1.z.string().regex(/^[a-f0-9]{64}$/),
    action: zod_1.z.enum(['weight.confirm', 'weight.ignore', 'weight.tare']),
    attendantId: Id,
    reason: zod_1.z.string().trim().min(1).max(2000),
    authorityRevision: Revision,
    delegationRevision: Revision,
    issuedAt: Instant,
    expiresAt: Instant,
})
    .strict()
    .superRefine((v, c) => {
    if (v.requestOrigin.tenantId !== v.tenantId ||
        v.requestOrigin.storeId !== v.storeId ||
        v.requestOrigin.terminalId !== v.targetTerminalId)
        c.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['requestOrigin'],
            message: 'Request scope mismatch',
        });
    const duration = Date.parse(v.expiresAt) - Date.parse(v.issuedAt);
    if (duration <= 0 ||
        duration > securescale_attendant_js_1.SECURESCALE_ATTENDANT_MAX_LIFETIME_MS ||
        Date.parse(v.expiresAt) >
            Math.min(Date.parse(v.sessionExpiresAt), Date.parse(v.requestExpiresAt)))
        c.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['expiresAt'],
            message: 'Command lifetime exceeds local session or open request',
        });
});
/** Station signs digest of canonical {kind,payload}; grants/delegation separately verified against enrolled BOS keys. */
exports.SecureScaleLocalCommandProof = zod_1.z
    .object({
    kind: zod_1.z.literal('securescale-local-command-signed-v1'),
    payload: exports.SecureScaleLocalCommandPayload,
    signature: Signature,
    delegation: exports.SecureScaleCommandDelegation,
    authority: securescale_attendant_js_1.SecureScaleAttendantAuthorization,
})
    .strict()
    .superRefine((v, c) => {
    const p = v.payload, d = v.delegation.payload, a = v.authority.payload;
    if (p.tenantId !== d.tenantId ||
        p.storeId !== d.storeId ||
        p.stationId !== d.stationId ||
        p.delegationRevision !== d.revision ||
        p.tenantId !== a.tenantId ||
        p.storeId !== a.storeId ||
        p.stationId !== a.terminalId ||
        p.authorityRevision !== a.revision)
        c.addIssue({ code: zod_1.z.ZodIssueCode.custom, message: 'Proof scope/revision mismatch' });
    if (Date.parse(p.expiresAt) > Math.min(Date.parse(d.expiresAt), Date.parse(a.expiresAt)) ||
        Date.parse(p.issuedAt) < Math.max(Date.parse(d.issuedAt), Date.parse(a.issuedAt)))
        c.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: 'Command outside signed authority lifetime',
        });
    const grant = a.grants.find((g) => g.cashierId === p.attendantId);
    if (!grant ||
        !grant.isAttendant ||
        grant.status !== 'active' ||
        !grant.pinHash ||
        !grant.permissionCodes.includes(p.action))
        c.addIssue({ code: zod_1.z.ZodIssueCode.custom, message: 'Genuine permitted attendant required' });
});
exports.SecureScaleCommandDirectoryPayload = zod_1.z
    .object({
    kind: zod_1.z.literal('securescale-station-command-directory-v1'),
    issuer: zod_1.z.literal('reform-bos'),
    audience: zod_1.z.literal('securescale-local-requester'),
    tenantId: Id,
    storeId: Id,
    terminalId: Id,
    revision: Revision,
    issuedAt: Instant,
    expiresAt: Instant,
    stations: zod_1.z
        .array(zod_1.z
        .object({
        stationId: Id,
        publicKey: PublicKey,
        revision: Revision,
        revocationFloor: Revision,
        status: zod_1.z.enum(['active', 'revoked']),
    })
        .strict())
        .max(10000),
})
    .strict()
    .superRefine((v, c) => {
    const duration = Date.parse(v.expiresAt) - Date.parse(v.issuedAt);
    if (duration <= 0 || duration > securescale_attendant_js_1.SECURESCALE_ATTENDANT_MAX_LIFETIME_MS)
        c.addIssue({ code: zod_1.z.ZodIssueCode.custom, message: 'Directory lifetime invalid' });
    if (new Set(v.stations.map((s) => s.stationId)).size !== v.stations.length)
        c.addIssue({ code: zod_1.z.ZodIssueCode.custom, message: 'Duplicate station' });
    for (const station of v.stations)
        if (BigInt(station.revocationFloor) > BigInt(station.revision))
            c.addIssue({ code: zod_1.z.ZodIssueCode.custom, message: 'Invalid station floor' });
});
exports.SecureScaleCommandDirectory = zod_1.z
    .object({
    kind: zod_1.z.literal('securescale-station-command-directory-signed-v1'),
    payload: exports.SecureScaleCommandDirectoryPayload,
    kid: Id,
    signature: Signature,
})
    .strict();
//# sourceMappingURL=securescale-local-command.js.map